<template>
  <div class="WalleyCheckout">
    <!-- Loading state -->
    <div v-if="isLoading" class="walley-loading flex items-center justify-center py-32">
      <div class="loading-spinner" />
      <span class="ml-12 type-sm text-darker">{{ loadingText }}</span>
    </div>

    <!-- Error state -->
    <div v-else-if="hasError" class="walley-error p-16 bg-criticalLight border border-criticalDark">
      <p class="type-sm text-criticalDark">{{ errorMessage }}</p>
      <button
        v-if="showRetry"
        class="btn btn--sm mt-12"
        type="button"
        @click="$emit('retry')"
      >
        {{ retryText }}
      </button>
    </div>

    <!-- Walley Checkout container -->
    <div v-else class="walley-wrapper relative">
      <div
        ref="walleyContainer"
        class="walley-container"
      />

      <!-- Validation Overlay - blocks interaction until checkout requirements are met -->
      <div
        v-if="!canCompletePurchase"
        class="walley-validation-overlay"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import type { WalleyPaymentData } from './types';
import { savePaymentSession } from './usePaymentSession';

const props = withDefaults(defineProps<{
  walleyPayment: WalleyPaymentData | null;
  basketUuid?: string;
  orderId?: string;
  paymentId?: string;
  isLoading?: boolean;
  loadingText?: string;
  errorMessage?: string;
  showRetry?: boolean;
  retryText?: string;
  canCompletePurchase?: boolean;
}>(), {
  basketUuid: '',
  orderId: '',
  paymentId: '',
  isLoading: false,
  loadingText: 'Loading Walley...',
  errorMessage: '',
  showRetry: true,
  retryText: 'Try again',
  canCompletePurchase: true,
});

const emit = defineEmits<{
  (e: 'retry'): void;
  (e: 'loaded'): void;
  (e: 'error', message: string): void;
}>();

const walleyContainer = ref<HTMLElement | null>(null);
const hasError = computed(() => Boolean(props.errorMessage));

/**
 * Save current payment session for Walley redirect flow
 * Walley redirects to payment-redirect without query params after completion
 */
const saveCurrentSession = () => {
  savePaymentSession({
    orderId: props.orderId || undefined,
    paymentId: props.paymentId || props.walleyPayment?.paymentId || undefined,
    basketUuid: props.basketUuid || undefined,
    paymentAdapter: 'walley',
  });
};

/**
 * Render the Walley Checkout widget
 * Walley's HTML snippet contains all necessary JS and handles the checkout flow
 *
 * Note: innerHTML doesn't execute script tags for security reasons.
 * Following the same pattern as Klarna for executing embedded scripts.
 */
const renderWalleyCheckout = async() => {
  if (!props.walleyPayment?.htmlSnippet || !walleyContainer.value) {
    return;
  }

  try {
    // Clear previous content
    walleyContainer.value.innerHTML = '';

    // Insert the HTML snippet (scripts will be in DOM but not executed)
    walleyContainer.value.innerHTML = props.walleyPayment.htmlSnippet;

    // Get all script tags and convert to static array
    // (getElementsByTagName returns a live collection that would cause infinite loop)
    const scriptTags = Array.from(walleyContainer.value.getElementsByTagName('script'));

    // Execute scripts manually
    for (const oldScript of scriptTags) {
      const parentNode = oldScript.parentNode;

      if (!parentNode) { continue; }

      // Create a new script element that will actually execute
      const newScript = document.createElement('script');
      newScript.type = 'text/javascript';
      newScript.text = oldScript.text;

      // Copy any attributes (like id, src, etc.)
      Array.from(oldScript.attributes).forEach((attr) => {
        if (attr.name !== 'type') {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      // Remove the old (inert) script and append the new (active) one
      parentNode.removeChild(oldScript);
      parentNode.appendChild(newScript);
    }

    await nextTick();
    emit('loaded');
  } catch (e) {
    console.error('Walley Checkout render error:', e);
    emit('error', 'Failed to load Walley Checkout');
  }
};

/**
 * Suspend Walley Checkout
 * Call this before making changes to the order (e.g., cart updates)
 */
const suspend = () => {
  // Walley may provide a similar API - implement if needed
  if (typeof window !== 'undefined' && (window as any)._walleyCheckout) {
    try {
      (window as any)._walleyCheckout((api: any) => {
        api.suspend?.();
      });
    } catch (e) {
      console.warn('Failed to suspend Walley Checkout:', e);
    }
  }
};

/**
 * Resume Walley Checkout
 * Call this after order changes are complete
 */
const resume = () => {
  // Walley may provide a similar API - implement if needed
  if (typeof window !== 'undefined' && (window as any)._walleyCheckout) {
    try {
      (window as any)._walleyCheckout((api: any) => {
        api.resume?.();
      });
    } catch (e) {
      console.warn('Failed to resume Walley Checkout:', e);
    }
  }
};

// Watch for changes in Walley payment data
watch(
  () => props.walleyPayment,
  async(newPayment) => {
    if (newPayment?.htmlSnippet) {
      // Wait for next tick to ensure ref is available after mount
      await nextTick();
      renderWalleyCheckout();
    }
  },
  { immediate: true }
);

// Also render on mount if data is already available
// This handles the case when the component mounts with data already present
onMounted(async() => {
  if (props.walleyPayment?.htmlSnippet) {
    // Save session for Walley redirect flow
    saveCurrentSession();
    await nextTick();
    renderWalleyCheckout();
  }
});

// Cleanup on unmount
onBeforeUnmount(() => {
  // Clear the container to prevent memory leaks
  if (walleyContainer.value) {
    walleyContainer.value.innerHTML = '';
  }
});

// Expose methods for parent component
defineExpose({
  suspend,
  resume,
  renderWalleyCheckout,
});
</script>

<style scoped lang="postcss">
.WalleyCheckout {
  @apply w-full;
}

.walley-wrapper {
  @apply relative;
}

.walley-container {
  @apply w-full min-h-[200px];
}

/* Validation overlay - blocks interaction until checkout requirements are met */
.walley-validation-overlay {
  @apply absolute inset-0 cursor-not-allowed z-10;
  background-color: rgba(255, 255, 255, 0.6);
}

.walley-loading {
  @apply min-h-[200px];
}

.loading-spinner {
  @apply w-24 h-24 border-2 border-light border-t-dark animate-spin;
  border-radius: 50%;
}
</style>
